import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "../../auth/AuthProvider";
import { EgressCard } from "../../components/EgressCard";
import { Alert, Button, Card, CardHeader, Input, cx } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { parseEnvText } from "../../lib/envText";
import { useEnvVars, useReplaceEnv } from "../../lib/queries";
import { useAppTabContext } from "./context";

/*
 * Shared by the column header and every row. `auto` on the last two tracks
 * keeps the checkbox and the Remove button hard against the right edge at any
 * width, and the value column takes whatever is left.
 */
const ROW_COLS =
  "gap-x-3 gap-y-2 sm:grid-cols-[minmax(0,16rem)_minmax(0,1fr)_auto_auto]";

interface Row {
  key: string;
  /** Empty on a secret loaded from the server means "leave it alone". */
  value: string;
  isSecret: boolean;
  storedSecret: boolean;
}

export function EnvTab() {
  const { t } = useTranslation("app");
  const { orgId, appId } = useAppTabContext();
  const canEdit = useCan("developer");
  const { data: envVars, isPending } = useEnvVars(orgId, appId);
  const replaceEnv = useReplaceEnv(orgId ?? "", appId ?? "");

  const [rows, setRows] = useState<Row[]>([]);
  const [redeploy, setRedeploy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [bulk, setBulk] = useState("");

  useEffect(() => {
    if (!envVars) return;
    setRows(
      envVars.map((v) => ({
        key: v.key,
        value: v.value ?? "",
        isSecret: v.isSecret,
        storedSecret: v.isSecret && v.value === null,
      })),
    );
  }, [envVars]);

  function update(index: number, patch: Partial<Row>) {
    setSaved(false);
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function addRow() {
    setRows((current) => [...current, { key: "", value: "", isSecret: false, storedSecret: false }]);
  }

  function removeRow(index: number) {
    setSaved(false);
    setRows((current) => current.filter((_, i) => i !== index));
  }

  function importBulk() {
    const parsed = parseEnvText(bulk);
    if (parsed.invalidLines.length > 0) {
      setError(t("env.parseFailed", { lines: parsed.invalidLines.join(", ") }));
      return;
    }
    setError(null);
    setRows((current) => {
      const merged = new Map(current.map((row) => [row.key, row]));
      for (const { key, value } of parsed.vars) {
        merged.set(key, { key, value, isSecret: false, storedSecret: false });
      }
      return [...merged.values()];
    });
    setBulk("");
  }

  async function save() {
    setError(null);
    setSaved(false);

    const trimmed = rows.filter((row) => row.key.trim() !== "");
    const duplicates = trimmed
      .map((row) => row.key.trim())
      .filter((key, index, all) => all.indexOf(key) !== index);
    if (duplicates.length > 0) {
      setError(`Duplicate keys: ${[...new Set(duplicates)].join(", ")}`);
      return;
    }

    try {
      await replaceEnv.mutateAsync({
        env: trimmed.map((row) => ({
          key: row.key.trim(),
          // null tells the API to keep the stored secret as-is.
          value: row.storedSecret && row.value === "" ? null : row.value,
          isSecret: row.isSecret,
        })),
        redeploy,
      });
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("env.failed"));
    }
  }

  if (isPending) return <Card className="p-5 text-sm text-fg-muted">{t("env.loading")}</Card>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title={t("env.title")}
          description={t("env.description")}
          action={
            canEdit ? (
              <div className="flex flex-wrap items-center justify-end gap-3">
                <label className="flex items-center gap-2 text-sm text-fg-muted">
                  <input
                    type="checkbox"
                    checked={redeploy}
                    onChange={(event) => setRedeploy(event.target.checked)}
                    className="size-4 accent-accent"
                  />
                  {t("env.redeployAfterSave")}
                </label>
                <Button onClick={() => void save()} loading={replaceEnv.isPending}>
                  {t("env.save")}
                </Button>
              </div>
            ) : undefined
          }
        />

        <div className="space-y-3 p-5">
          {error && <Alert>{error}</Alert>}
          {saved && (
            <Alert tone="success">
              {redeploy ? t("env.savedRedeploy") : t("env.saved")}
            </Alert>
          )}

          {rows.length === 0 && (
            <p className="py-4 text-sm text-fg-subtle">
              {t("env.empty")}
            </p>
          )}

          {/*
            One grid template, declared once and shared by the header and every
            row, so the columns line up by construction. This was a flex row per
            variable: the columns only agreed because every row happened to
            contain the same things, and below `sm` the four controls wrapped
            into an unlabelled pile.
          */}
          {rows.length > 0 && (
            <div className={cx("hidden sm:grid", ROW_COLS)}>
              <span className="micro">{t("env.key")}</span>
              <span className="micro">{t("env.value")}</span>
              <span className="micro">{t("env.secret")}</span>
              <span className="sr-only">{t("env.actions")}</span>
            </div>
          )}

          {rows.map((row, index) => (
            <div
              key={index}
              className={cx(
                "grid border-b border-line pb-3 last:border-0 last:pb-0",
                "sm:items-center sm:border-0 sm:pb-0",
                ROW_COLS,
              )}
            >
              <div className="space-y-1 sm:contents">
                <span className="micro sm:hidden">{t("env.key")}</span>
                <Input
                  aria-label={t("env.key")}
                  value={row.key}
                  disabled={!canEdit}
                  onChange={(event) => update(index, { key: event.target.value })}
                  placeholder={t("env.keyPlaceholder")}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1 sm:contents">
                <span className="micro sm:hidden">{t("env.value")}</span>
                <Input
                  aria-label={t("env.value")}
                  type={row.isSecret ? "password" : "text"}
                  value={row.value}
                  disabled={!canEdit}
                  onChange={(event) => update(index, { value: event.target.value })}
                  placeholder={row.storedSecret ? t("env.valueUnchanged") : t("env.valuePlaceholder")}
                  className="font-mono"
                />
              </div>
              {/* The label text is carried by the column header at `sm` and up,
                  and by the label itself below it, where there is no header. */}
              <label className="flex items-center gap-2 text-sm text-fg-muted">
                <input
                  type="checkbox"
                  checked={row.isSecret}
                  disabled={!canEdit}
                  onChange={(event) => update(index, { isSecret: event.target.checked })}
                  className="size-4 accent-accent"
                />
                <span className="sm:hidden">{t("env.secret")}</span>
              </label>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-self-start sm:justify-self-auto"
                  onClick={() => removeRow(index)}
                >
                  {t("env.remove")}
                </Button>
              )}
            </div>
          ))}

          {canEdit && (
            <Button variant="secondary" size="sm" onClick={addRow}>
              {t("env.add")}
            </Button>
          )}
        </div>
      </Card>

      {/*
        Beside the bulk import rather than above the list: somebody pasting a
        .env with a DATABASE_URL pointing into their own network is exactly who
        needs this, and putting it first would push the variables they came for
        below the fold.
      */}
      <EgressCard orgId={orgId} />

      {canEdit && (
        <Card>
          <CardHeader title={t("env.bulk")} description={t("env.bulkDescription")} />
          <div className="space-y-3 p-5">
            <textarea
              rows={5}
              spellCheck={false}
              value={bulk}
              onChange={(event) => setBulk(event.target.value)}
              placeholder={"NODE_ENV=production\nDATABASE_URL=postgres://…"}
              className="w-full rounded-lg border border-line-strong bg-canvas px-3 py-2 font-mono text-sm pointer-coarse:text-base text-fg"
            />
            <Button variant="secondary" size="sm" onClick={importBulk} disabled={bulk.trim() === ""}>
              {t("env.merge")}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
